import { useMemo } from 'react';

import { useAuth } from '@context/auth';

import { FormValues } from '../../../types';
import { useCatalogFilters } from '../../../useCatalogFilters';

interface UseFiltersDataParams {
    preparedFormFilters: FormValues | null;
    preparedFiltersWithoutLast: FormValues | null;
    baseFilters: FormValues | null;
    isOpen: boolean;
    shouldMakeRequest: boolean;
}

export const useFiltersData = ({
    preparedFormFilters,
    preparedFiltersWithoutLast,
    baseFilters,
    isOpen,
    shouldMakeRequest,
}: UseFiltersDataParams) => {
    const { isAuthUser } = useAuth();
    const isUserAuth = Boolean(isAuthUser);

    const {
        filtersListData: currentFiltersData,
        isFilterFetching: isCurrentFetching,
        isFiltersAuthFetching: isCurrentFetchingAuth,
        error: currentError,
    } = useCatalogFilters(preparedFormFilters, {
        enabled: !isUserAuth && shouldMakeRequest,
        enabledAuth: isUserAuth && shouldMakeRequest,
        useUrlFilters: false,
    });

    const {
        filtersListData: previousFiltersData,
        isFilterFetching: isPreviousFetching,
        isFiltersAuthFetching: isPreviousFetchingAuth,
        error: previousError,
    } = useCatalogFilters(preparedFiltersWithoutLast, {
        enabled: !isUserAuth && shouldMakeRequest && Boolean(preparedFiltersWithoutLast),
        enabledAuth: isUserAuth && shouldMakeRequest && Boolean(preparedFiltersWithoutLast),
        useUrlFilters: false,
    });

    const {
        filtersListData: baseFiltersData,
        isFilterFetching: isBaseFetching,
        isFiltersAuthFetching: isBaseFetchingAuth,
        error: baseError,
    } = useCatalogFilters(baseFilters, {
        enabled: !isUserAuth && Boolean(isOpen),
        enabledAuth: isUserAuth && Boolean(isOpen),
        useUrlFilters: false,
    });

    const isLoading = useMemo(() => {
        if (isUserAuth) {
            return isCurrentFetchingAuth || isPreviousFetchingAuth || isBaseFetchingAuth;
        }

        return isCurrentFetching || isPreviousFetching || isBaseFetching;
    }, [
        isUserAuth,
        isCurrentFetching,
        isPreviousFetching,
        isBaseFetching,
        isCurrentFetchingAuth,
        isPreviousFetchingAuth,
        isBaseFetchingAuth,
    ]);

    const hasError = useMemo(() => {
        return !!(currentError || previousError || baseError);
    }, [currentError, previousError, baseError]);

    return {
        currentFiltersData,
        previousFiltersData,
        baseFiltersData,
        isLoading,
        hasError,
        currentError,
        previousError,
        baseError,
    };
};
